/**
 * Normalizes tag criteria from both API `rule_groups` format and legacy `and/or` format
 * into human-readable display strings.
 */

const PARAM_LABELS: Record<string, string> = {
  active: "Active Status",
  os_type: "OS Type",
  os_version: "OS Version",
  platform: "Platform",
  modelName: "Model",
  model: "Model",
  deviceName: "Device Name",
  device_name: "Device Name",
  status: "Status",
  serialNumber: "Serial Number",
  serial_number: "Serial Number",
  manufacturer: "Manufacturer",
  osVersion: "OS Version",
  compliance: "Compliance",
  enrollment_type: "Enrollment Type",
  supervised: "Supervised",
  model_name: "Model Name",
  product_name: "Product Name",
};

const API_OPERATOR_LABELS: Record<string, string> = {
  EQ: "=",
  NEQ: "≠",
  GT: ">",
  LT: "<",
  GTE: "≥",
  LTE: "≤",
  CONTAINS: "contains",
  NOT_CONTAINS: "not contains",
  STARTS_WITH: "starts with",
  ENDS_WITH: "ends with",
  IN: "in",
  NOT_IN: "not in",
};

const LEGACY_OPERATOR_LABELS: Record<string, string> = {
  equals: "=",
  not_equals: "≠",
  contains: "contains",
  not_contains: "not contains",
  starts_with: "starts with",
  ends_with: "ends with",
  greater_than: ">",
  less_than: "<",
  in: "in",
  not_in: "not in",
};

export interface NormalizedCriteria {
  logic: string;
  conditions: string[];
}

function formatValue(value: string): string {
  if (value === "true") return "Yes";
  if (value === "false") return "No";
  return value;
}

function parseRuleGroups(criteria: any): NormalizedCriteria | null {
  const ruleGroups = criteria?.rule_groups || criteria?.tag_criteria?.rule_groups;
  if (!Array.isArray(ruleGroups) || ruleGroups.length === 0) return null;

  const conditions: string[] = [];

  for (const group of ruleGroups) {
    const rules = group.rules || [];
    const groupConditions: string[] = [];

    for (const rule of rules) {
      const field = PARAM_LABELS[rule.param] || rule.param || "?";
      const op = API_OPERATOR_LABELS[rule.operator] || rule.operator || "?";
      const val = formatValue(rule.value || "?");
      groupConditions.push(`${field} ${op} ${val}`);
    }

    if (groupConditions.length > 1) {
      conditions.push(`(${groupConditions.join(" AND ")})`);
    } else if (groupConditions.length === 1) {
      conditions.push(groupConditions[0]);
    }
  }

  if (conditions.length === 0) return null;

  return {
    logic: ruleGroups.length > 1 ? "OR" : "AND",
    conditions,
  };
}

function parseLegacyFormat(criteria: any): NormalizedCriteria | null {
  const logic = criteria.and ? "AND" : criteria.or ? "OR" : null;
  if (!logic) return null;

  const raw = criteria.and || criteria.or || [];
  const conditions: string[] = [];

  for (const item of raw) {
    if (item.field) {
      const field = PARAM_LABELS[item.field] || item.field;
      const op = LEGACY_OPERATOR_LABELS[item.operator] || item.operator || "?";
      const val = formatValue(item.value || "?");
      conditions.push(`${field} ${op} ${val}`);
    } else if (item.and || item.or) {
      const subLogic = item.and ? "AND" : "OR";
      const subs = (item.and || item.or || [])
        .filter((c: any) => c.field)
        .map((c: any) => {
          const f = PARAM_LABELS[c.field] || c.field;
          const o = LEGACY_OPERATOR_LABELS[c.operator] || c.operator || "?";
          const v = formatValue(c.value || "?");
          return `${f} ${o} ${v}`;
        });
      if (subs.length) conditions.push(`(${subs.join(` ${subLogic} `)})`);
    }
  }

  return conditions.length > 0 ? { logic, conditions } : null;
}

/**
 * Normalizes tag criteria from any known format into a display-friendly structure.
 * Handles:
 * - API `rule_groups` format (from backend)
 * - Legacy `{ and: [...] }` / `{ or: [...] }` format (from builder)
 * - String JSON of either format
 */
export function normalizeCriteria(criteria: any): NormalizedCriteria | null {
  if (!criteria) return null;

  try {
    const parsed = typeof criteria === "string" ? JSON.parse(criteria) : criteria;
    if (!parsed || typeof parsed !== "object") return null;

    // Try API rule_groups format first
    const fromRuleGroups = parseRuleGroups(parsed);
    if (fromRuleGroups) return fromRuleGroups;

    // Fall back to legacy and/or format
    return parseLegacyFormat(parsed);
  } catch {
    return null;
  }
}

/**
 * Convert API rule_groups format into the internal CriteriaStructure used by TagCriteriaBuilder.
 */
export function ruleGroupsToCriteriaStructure(criteria: any): {
  groups: { logic: "and" | "or"; conditions: { field: string; operator: string; value: string }[] }[];
  operators: ("and" | "or")[];
} | null {
  const parsed = typeof criteria === "string" ? JSON.parse(criteria) : criteria;
  const ruleGroups = parsed?.rule_groups || parsed?.tag_criteria?.rule_groups;
  if (!Array.isArray(ruleGroups) || ruleGroups.length === 0) return null;

  // Map API operators back to legacy builder operators
  const opMap: Record<string, string> = {
    EQ: "equals",
    NEQ: "not_equals",
    GT: "greater_than",
    LT: "less_than",
    GTE: "greater_than_or_equal",
    LTE: "less_than_or_equal",
    CONTAINS: "contains",
    NOT_CONTAINS: "not_contains",
    STARTS_WITH: "starts_with",
    ENDS_WITH: "ends_with",
    IN: "in",
    NOT_IN: "not_in",
  };

  const groups = ruleGroups.map((group: any) => ({
    logic: "and" as const,
    conditions: (group.rules || []).map((rule: any) => ({
      field: rule.param || "",
      operator: opMap[rule.operator] || rule.operator?.toLowerCase() || "",
      value: rule.value || "",
    })),
  }));

  const operators: ("and" | "or")[] = ruleGroups.length > 1
    ? Array(ruleGroups.length - 1).fill("or")
    : [];

  return { groups, operators };
}
